import React from 'react';
import { createPortal } from 'react-dom';
import { fetchLocations } from '../../data/locations';
import { fetchBroadcastLocationsForShortage } from '../../data/transfers';
import { useToasts } from '../../ui/ToastProvider';
import { CloseIcon } from '../../ui/CloseIcon';
import { updateTransferBroadcast } from '../../api/transfers';
import { formatSupabaseUiError } from '../../utils/supabaseErrors';

type UpdateTransferBroadcastModalProps = {
  open: boolean;
  broadcast: {
    broadcast_id: string;
    shortage_id?: string;
    qty: number;
    needed_by_at: string;
    expected_return_at: string;
    to_location_id: string;
    shortage?: {
      location_id?: string;
    };
  } | null;
  onClose: () => void;
  onUpdated?: () => void;
};

export default function UpdateTransferBroadcastModal({
  open,
  broadcast,
  onClose,
  onUpdated,
}: UpdateTransferBroadcastModalProps) {
  const { push } = useToasts();
  const [qty, setQty] = React.useState<string>('');
  const [neededByAt, setNeededByAt] = React.useState<string>('');
  const [expectedReturnAt, setExpectedReturnAt] = React.useState<string>('');
  const [candidateLocationIds, setCandidateLocationIds] = React.useState<string[]>([]);
  const [pendingLocationId, setPendingLocationId] = React.useState<string>('');
  const [locations, setLocations] = React.useState<Array<{ location_id: string; name: string }>>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const titleId = React.useId();

  // Load locations
  React.useEffect(() => {
    if (open) {
      setError(null);
      fetchLocations(null).then(({ data, error }) => {
        if (error) {
          setError(formatSupabaseUiError(error, 'Unable to load transfer locations.'));
          return;
        }
        setLocations(data || []);
      });
    }
  }, [open]);

  // Initialize form from broadcast data
  React.useEffect(() => {
    if (open && broadcast) {
      setQty(String(broadcast.qty || ''));
      
      // Convert timestamptz to datetime-local format
      if (broadcast.needed_by_at) {
        const needByDate = new Date(broadcast.needed_by_at);
        const year = needByDate.getFullYear();
        const month = String(needByDate.getMonth() + 1).padStart(2, '0');
        const day = String(needByDate.getDate()).padStart(2, '0');
        const hours = String(needByDate.getHours()).padStart(2, '0');
        const minutes = String(needByDate.getMinutes()).padStart(2, '0');
        setNeededByAt(`${year}-${month}-${day}T${hours}:${minutes}`);
      } else {
        setNeededByAt('');
      }
      
      if (broadcast.expected_return_at) {
        const returnDate = new Date(broadcast.expected_return_at);
        const year = returnDate.getFullYear();
        const month = String(returnDate.getMonth() + 1).padStart(2, '0');
        const day = String(returnDate.getDate()).padStart(2, '0');
        const hours = String(returnDate.getHours()).padStart(2, '0');
        const minutes = String(returnDate.getMinutes()).padStart(2, '0');
        setExpectedReturnAt(`${year}-${month}-${day}T${hours}:${minutes}`);
      } else {
        setExpectedReturnAt('');
      }
      
      // Initialize with current to_location_id
      setCandidateLocationIds([broadcast.to_location_id]);
      setPendingLocationId('');
      
      // Fetch all broadcasts for this shortage to get all candidate locations
      if (broadcast.broadcast_id && broadcast.shortage_id) {
        fetchBroadcastLocationsForShortage(broadcast.shortage_id).then(({ data, error }) => {
          if (error) {
            setError(formatSupabaseUiError(error, 'Unable to load candidate locations.'));
            return;
          }
          if (data.length > 0) {
            setCandidateLocationIds(data);
          }
        });
      }
    }
  }, [open, broadcast]);

  const availableCandidateLocations = React.useMemo(
    () => locations.filter(loc => !candidateLocationIds.includes(loc.location_id)),
    [locations, candidateLocationIds],
  );

  const handleAddLocation = React.useCallback(() => {
    if (availableCandidateLocations.length === 0) {
      push({ type: 'info', message: 'All locations are already added' });
      return;
    }
    if (!pendingLocationId) {
      setError('Select a location to add.');
      return;
    }
    setCandidateLocationIds(prev => [...prev, pendingLocationId]);
    setPendingLocationId('');
    setError(null);
  }, [availableCandidateLocations.length, pendingLocationId, push]);

  const handleRemoveLocation = React.useCallback((locationId: string) => {
    if (candidateLocationIds.length <= 1) {
      push({ type: 'warn', message: 'At least one candidate location is required' });
      return;
    }
    setCandidateLocationIds(prev => prev.filter(id => id !== locationId));
  }, [candidateLocationIds, push]);

  const handleSubmit = React.useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!broadcast) {
      setError('No broadcast selected');
      return;
    }
    
    const qtyNum = parseInt(qty, 10);
    if (isNaN(qtyNum) || qtyNum <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }
    
    if (!neededByAt || !expectedReturnAt) {
      setError('Both need-by and expected return dates are required');
      return;
    }
    
    const needByDate = new Date(neededByAt);
    const returnDate = new Date(expectedReturnAt);
    
    if (needByDate >= returnDate) {
      setError('Need-by date must be before expected return date');
      return;
    }
    
    if (candidateLocationIds.length === 0) {
      setError('At least one candidate location is required');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      await updateTransferBroadcast(broadcast.broadcast_id, {
        qty: qtyNum,
        neededByAt: needByDate.toISOString(),
        expectedReturnAt: returnDate.toISOString(),
        candidateLocationIds,
      });
      
      push({ type: 'success', message: 'Transfer broadcast updated. Notifications sent to relevant locations.' });
      onUpdated?.();
      onClose();
    } catch (err: unknown) {
      const message = formatSupabaseUiError(err, 'Unable to update transfer request.');
      setError(message);
      push({ type: 'error', message });
    } finally {
      setSubmitting(false);
    }
  }, [broadcast, qty, neededByAt, expectedReturnAt, candidateLocationIds, push, onUpdated, onClose]);

  if (!open || !broadcast) return null;

  const modalContent = (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '600px', width: '90%' }}
      >
        <div className="modal-header">
          <h2 id={titleId}>Update Transfer Request</h2>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="stack gap-md" style={{ padding: '1.5rem' }}>
          {error && (
            <div className="alert alert-error" role="alert">{error}</div>
          )}
          
          <label className="stack gap-xxs">
            <span>Quantity *</span>
            <input
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="control"
              required
            />
          </label>
          
          <label className="stack gap-xxs">
            <span>Need-by Date *</span>
            <input
              type="datetime-local"
              value={neededByAt}
              onChange={(e) => setNeededByAt(e.target.value)}
              className="control"
              required
            />
          </label>
          
          <label className="stack gap-xxs">
            <span>Expected Return Date *</span>
            <input
              type="datetime-local"
              value={expectedReturnAt}
              onChange={(e) => setExpectedReturnAt(e.target.value)}
              className="control"
              required
            />
          </label>
          
          <div className="stack gap-xxs">
            <span>Candidate Locations *</span>
            <div className="stack gap-xs">
              {candidateLocationIds.map((locId) => {
                const loc = locations.find(l => l.location_id === locId);
                return (
                  <div key={locId} className="flex gap-sm" style={{ alignItems: 'center' }}>
                    <span>{loc?.name || locId}</span>
                    {candidateLocationIds.length > 1 && (
                      <button
                        type="button"
                        className="btn xs subtle"
                        onClick={() => handleRemoveLocation(locId)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
              {availableCandidateLocations.length > 0 ? (
                <div className="flex gap-xs" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className="control"
                    value={pendingLocationId}
                    onChange={(event) => setPendingLocationId(event.target.value)}
                    style={{ flex: '1 1 220px' }}
                  >
                    <option value="">Select location...</option>
                    {availableCandidateLocations.map(location => (
                      <option key={location.location_id} value={location.location_id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn xs"
                    onClick={handleAddLocation}
                    disabled={!pendingLocationId}
                  >
                    Add Location
                  </button>
                </div>
              ) : (
                <p className="m0 text-xs fg-muted">All locations are already added.</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-sm" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Updating...' : 'Update Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
